/**
 * フォームのデータを操作するロジックをまとめる
 */
import { initialAnnounce } from '@/appLogic/importData/initialData';
import { ISelectFormDataSet } from '@/coreDomainsModule/table';
import { DEFAULT_WEEK_SCALE, DEFAULT_MONTH_SCALE, FORECAST_LENGTH } from '@/coreDomainsModule/constants';
import { splitNumberFormat } from '@/utils/display/numbers';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { IAnnounce } from '@/coreDomainsModule/entities/forecast';
import { IDate, IYearMonth } from '@/coreDomainsModule/date';
import { ISalesResult } from '@/coreDomainsModule/entities/salesResults';
import { IArrival, OrderStatus } from '@/coreDomainsModule/entities/arrival';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { i18n } from '@/i18n';
/**
 * (日、週、月のデータを日データへ変換する)
 */

const t = i18n.t;

/**
 *
 * @param selectFormSet
 * @param weekScale
 * @param monthScale
 */
export const calculateDataToDays = (selectFormSet: ISelectFormDataSet, weekScale?: number, monthScale?: number): number => {
  const displayData = selectFormSet.displayData;
  if (!displayData) {
    return 0;
  }
  const selectedUnit = selectFormSet.selectedUnit;
  switch (selectedUnit) {
    case 'day':
      return displayData;
    case 'week':
      return (weekScale ? displayData * weekScale : displayData * DEFAULT_WEEK_SCALE) as number;
    case 'month':
      return (monthScale ? displayData * monthScale : displayData * DEFAULT_MONTH_SCALE) as number;
  }
  return displayData;
};

/**
 *
 * @param displayData
 * @param unit
 * @param weekScale
 * @param monthScale
 * TODO: 関数名を修正する
 */
export const daysTocalculateDate = (displayData: number, unit: string, weekScale?: number, monthScale?: number): ISelectFormDataSet => {
  let targetData;
  switch (unit) {
    case 'day':
      targetData = displayData;
      break;
    case 'week':
      targetData = (weekScale ? displayData / weekScale : displayData / DEFAULT_WEEK_SCALE) as number;
      break;
    case 'month':
      targetData = (monthScale ? displayData / monthScale : displayData / DEFAULT_MONTH_SCALE) as number;
  }
  return { displayData: targetData, selectedUnit: unit };
};

/**
 *
 * @param formSet
 * @param selectFormSet
 * @param formAttr
 * @param weekScale
 * @param monthScale
 */
export const inputDataToDays = (
  formSet: IBusiness,
  selectFormSet: ISelectFormDataSet,
  formAttr: string,
  weekScale?: number,
  monthScale?: number
) => {
  const displayData = selectFormSet.displayData;
  if (!selectFormSet.displayData || selectFormSet.displayData < 0) return;
  formSet[formAttr] = calculateDataToDays(selectFormSet);
};

/**
 * Date関連の値を年月日のオブジェクトに変換する
 * @param date
 */
export const dateStrToObject = (date: string | Date | number): IDate => {
  const targetDate = new Date(date);
  const year = targetDate.getFullYear();
  const month = targetDate.getMonth() + 1;
  const day = targetDate.getDate();
  return { year: year, month: month, day: day };
};

/**
 * 年月日のオブジェクトをUNIXTIMEに変換する
 * @param date
 */
export const dateObjToUnixTime = (date: IDate): number => {
  return new Date(date.year, date.month - 1, date.day).getTime();
};

/**
 * Date関連の値をUNIXTIMEに変換する
 * @param date
 */
export const dateStrToUnixTime = (date: string | Date | number): number => {
  return dateObjToUnixTime(dateStrToObject(date));
};

/**
 * { year, month, day } のオブジェクトを『20210124』 or 『2021-01-24』のstring値で返す
 * @param dateObj
 * @param includeHyphen
 */
export const dateObjectToStr = (dateObj: IDate | undefined, includeHyphen?: boolean) => {
  if (dateObj === undefined) {
    return '';
  }
  const month = String(dateObj.month).length === 1 ? `0${dateObj.month}` : `${dateObj.month}`;
  const day = String(dateObj.day).length === 1 ? `0${dateObj.day}` : `${dateObj.day}`;
  if (includeHyphen ?? false) {
    return `${dateObj.year}-${month}-${day}`;
  } else {
    return `${dateObj.year}${month}${day}`;
  }
};

/**
 * { year, month } のオブジェクトを『202101』のstring値で返す
 * @param dateObj
 */
export const yearMonthToStr = (dateObj: IYearMonth) => {
  const month = String(dateObj.month).length === 1 ? `0${dateObj.month}` : `${dateObj.month}`;
  return `${dateObj.year}${month}`;
};

/**
 * 当日からperiodたったものを考える
 * @param period
 */
export const getTargetPeriodCalendar = async (period: number) => {
  const now = new Date();
};

// /**
//  *
//  * @param businesses
//  * @param period
//  * @param calendar
//  * @param now
//  */
// export const checkMinusInventory = (
//   businesses: IBusiness[],
//   period: number,
//   calendar: ICalendar,
//   now = new Date()
// ): { state: boolean; errors: IBusiness[] } => {
//   const dayStd = DAY_STD;
//   const nowTime = now.getTime();
//   const targetDate = new Date(nowTime + dayStd * period);
//   const targetWorkingDate = getWorkingDateWithPeriod(
//     { year: targetDate.getFullYear(), month: targetDate.getMonth() + 1, day: targetDate.getDate() },
//     period,
//     calendar
//   );
//   const errors = [] as IBusiness[];
//   if (!targetWorkingDate) {
//     console.error('not workingCalendar');
//     return { state: false, errors: [] };
//   }
//   for (let i = 0; i < businesses.length; i++) {
//     if (businesses[i].minusInventory) {
//       const inspectDate = businesses[i].minusInventory?.date as IDate;
//       if (compareIDate(inspectDate, targetWorkingDate as IDate) < 0) {
//         errors.push(businesses[i]);
//       }
//     }
//   }
//   return { state: !(errors.length > 0), errors: errors };
// };

/**
 * 前月の入荷・販売実績情報の数量値を取得
 * @param content 入荷情報か販売実績情報
 * @returns 数量
 */
export const getArrivalOrSalesResultValue = (content: ISalesResult[] | IArrival[]): number | undefined => {
  // 処理実行日の情報を生成する
  const today = new Date();
  const month = today.getMonth();
  const year = today.getFullYear();
  // 前月情報の計算
  const lastMonth = (month + 11) % 12;
  const lastYear = year - Math.floor(lastMonth / 11); // 前月算出の年跨ぎ対応
  const yearMonthInfo: IYearMonth = {
    year: lastYear,
    month: lastMonth + 1,
  };
  const result = content.filter((item) => DateService.equalYearMonthInfo(item, yearMonthInfo)); // 月は0~11で表現されているため+1している
  if (result.length > 0) {
    return result[0].amount;
  } else {
    return undefined;
  }
};

/**
 * テーブルに表示するための前月の入荷・販売実績情報の取得を行う
 * @param content 入荷情報か販売実績情報
 */
export const getDisplayArrivalOrSalesResultLabel = (content: ISalesResult[] | IArrival[]): string => {
  const displayValue: string | null = splitNumberFormat(getArrivalOrSalesResultValue(content));
  if (!displayValue) return '-';
  return displayValue;
};

/**
 * テーブルに表示するための前月の入荷・販売実績情報の取得を行う
 * @param content 入荷情報か販売実績情報
 */
export const getDisplayOrderStatusString = (content: IArrival[]): string => {
  if (content.length === 0) return '未発注';
  const orderStatus = content[0].status;
  if (orderStatus === undefined) return t(OrderStatus.Recommended);
  return t(orderStatus);
};

/**
 * 前月の入荷・販売実績の列名を生成する
 * @param itemType 入荷か販売実績か
 */
export const getArrivalOrSalesResultLabel = (itemType: 'arrivals' | 'salesResults'): string => {
  // 処理実行日の情報を生成する
  const today = new Date();
  const month = today.getMonth();
  // 前月情報の計算
  const lastMonth = ((month + 11) % 12) + 1;
  if (itemType === 'arrivals') {
    return `${lastMonth}月入荷`;
  } else {
    return `${lastMonth}月販売実績`;
  }
};

/**
 * 集計行に属する子取引を取得する
 * @param parent 集計行にあたる親取引
 * @param businesses 子取引を抽出する元の取引群
 * @returns 子取引の配列
 */
export const getAggregateGroups = (parent: IBusiness, businesses: IBusiness[]): IBusiness[] => {
  const groups = businesses.filter((b) => parent.aggregateGroup?.includes(b.businessId));
  return groups;
};

export const typesOfIBusiness = {
  organizationId: 'string',
  dataId: 'string',
  businessId: 'string',
  endUser: 'string',
  supplierName: 'string',
  deliveryDestinationName: 'string',
  salesDestinationName: 'string',
  businessName: 'string',
  unit: 'string',
  form: 'number',
  MOQ: 'number',
  note: 'string',
  salesResults: 'object',
  leadTime: 'number',
  stdInventoryIndex: 'number',
  inventory: 'number',
  forecast: 'object',
  orderAmount: 'number',
  orderDate: 'string',
  deliveryDate: 'object',
  group: 'object',
  isFixOrdered: 'boolean',
  isTrashed: 'boolean',
  virtualChild: 'boolean',
  isDeleted: 'boolean',
  updatedAt: 'number',
  fixOrderDate: 'object',
};

/*
確定発注のExcel出力情報のデフォルト値
*/
export const outputExcelInfo = {
  businessId: { displayCell: 'B1', isDisplay: true },
  endUser: { displayCell: 'C1', isDisplay: true },
  salesDestinationName: { displayCell: 'P1', isDisplay: true },
  supplierName: { displayCell: 'D1', isDisplay: true },
  deliveryDestinationName: { displayCell: 'E1', isDisplay: true },
  businessName: { displayCell: 'F1', isDisplay: true },
  note: { displayCell: 'G1', isDisplay: true },
  unit: { displayCell: 'H1', isDisplay: true },
  form: { displayCell: 'I1', isDisplay: true },
  leadTime: { displayCell: 'J1', isDisplay: true },
  MOQ: { displayCell: 'K1', isDisplay: true },
  stdInventoryIndex: { displayCell: 'L1', isDisplay: true },
  fixOrderDate: { displayCell: 'M1', isDisplay: true },
  orderAmount: { displayCell: 'N1', isDisplay: true },
  deliveryDate: { displayCell: 'O1', isDisplay: true },
};

/*
会社情報のデフォルト値
*/
export const defaultCompanyInfo = {
  companyName: {
    column: '社名',
    displayCell: 'G1',
    isDeletable: false,
  },
  postCode: {
    column: '郵便番号',
    displayCell: 'B1',
    isDeletable: false,
  },
  address: {
    column: '住所',
    displayCell: 'D1',
    isDeletable: false,
  },
  staffName: {
    column: '担当社名',
    displayCell: 'A1',
    isDeletable: false,
  },
  tel: {
    column: '電話番号',
    displayCell: 'C1',
    isDeletable: false,
  },
  fax: {
    column: 'FAX番号',
    displayCell: 'F1',
    isDeletable: false,
  },
};

/*
IBusinessに対する検索対象項目
*/
export const searchableLabelDictionary = {
  deliveryDestinationName: true,
  endUser: true,
  note: true,
  businessName: true,
  salesDestinationName: true,
  supplierName: true,
  unit: true,
};
